import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ShieldCheck, FileText, ChevronLeft } from "lucide-react";
import { useAuth } from "../lib/auth";
import SignaturePad from "../components/SignaturePad";
import { fmtDate } from "../lib/datetime";

const GRADE_BADGE = {
  CAP: "badge-green",
  AN: "badge-blue",
  M: "badge-blue",
  NI: "badge-amber",
  PA: "badge-red",
  SC: "badge-red",
  "N/A": "badge-slate",
  "": "badge-slate",
};

const SC_PA_COLOUR = { PA: "#dc2626", SC: "#7f1d1d" };

export default function Inspections() {
  const { user } = useAuth();
  const canInspect = ["admin", "contracts_manager", "supervisor", "competent_person"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(null); // structure being inspected
  const [success, setSuccess] = useState(null);

  const load = () => api.get("/inspections/due").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const overdue = rows.filter((r) => r.overdue);
  const onCycle = rows.filter((r) => !r.overdue);

  if (picked) {
    return (
      <InspectionForm
        structure={picked}
        onCancel={() => setPicked(null)}
        onSubmitted={(inspection) => {
          setPicked(null);
          setSuccess(inspection);
          load();
        }}
      />
    );
  }

  return (
    <div data-testid="inspections-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 20 }}>
        <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>WEEKLY CYCLE · ITEMISED REPORT</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>Inspection tracker</h1>
        <div style={{ color: "#64748b", marginTop: 4 }}>Overdue items pinned. Recording opens the full Appendix-A checklist; any SC grade tags the scaffold out automatically.</div>
      </header>

      {success ? (
        <div data-testid="inspection-success" className="card" style={{ padding: 14, marginBottom: 16, borderLeft: `6px solid ${success.overall_result === "Pass" ? "#059669" : "#dc2626"}`, background: success.overall_result === "Pass" ? "#ecfdf5" : "#fef2f2" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: success.overall_result === "Pass" ? "#065f46" : "#b91c1c" }}>
            {success.overall_result === "Pass" ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />} Inspection {success.report_no} · {success.overall_result}{" "}
            · next due {fmtDate(success.due_date)}
          </div>
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <a data-testid="inspection-pdf-link" className="btn-primary" href={`${API}/inspections/${success.id}/pdf`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <FileText size={14} /> View PDF report
            </a>
            <button className="btn-ghost" onClick={() => setSuccess(null)}>Dismiss</button>
          </div>
        </div>
      ) : null}

      <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#b91c1c", margin: "0 0 8px" }}>Overdue ({overdue.length})</h2>
      <Table rows={overdue} pick={canInspect ? setPicked : null} overdue testid="overdue-table" />

      <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "28px 0 8px" }}>On cycle ({onCycle.length})</h2>
      <Table rows={onCycle} pick={canInspect ? setPicked : null} testid="oncycle-table" />
    </div>
  );
}

function Table({ rows, pick, overdue, testid }) {
  return (
    <div className="card" style={{ padding: 0 }} data-testid={testid}>
      <table>
        <thead>
          <tr><th>Status</th><th>Structure</th><th>Area</th><th>Last inspected</th><th>Next due</th>{pick ? <th></th> : null}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`insp-due-row-${r.id}`}>
              <td>{r.overdue ? <span className="badge badge-red"><AlertTriangle size={11} /> Overdue</span> : <span className="badge badge-green">On cycle</span>}</td>
              <td><Link to={`/structures/${r.id}`} style={{ color: "#0f172a", fontWeight: 600 }}>{r.description}</Link><div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>{r.price_schedule_ref} · {r.quantity} {r.unit}</div></td>
              <td>{r.area_name}<div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>{r.zone_classification}</div></td>
              <td className="mono">{r.last_inspection_date ? fmtDate(r.last_inspection_date) : "—"}</td>
              <td className="mono" style={{ color: r.overdue ? "#b91c1c" : "#0f172a", fontWeight: 600 }}>{r.next_due ? fmtDate(r.next_due) : "—"}</td>
              {pick ? <td><button data-testid={`insp-record-${r.id}`} className="btn-primary" onClick={() => pick(r)}><ShieldCheck size={12} style={{ verticalAlign: -2, marginRight: 4 }} /> Inspect</button></td> : null}
            </tr>
          ))}
          {rows.length === 0 ? <tr><td colSpan={pick ? 6 : 5} style={{ textAlign: "center", padding: 20, color: "#94a3b8" }}>{overdue ? "Nothing overdue · KPI green" : "No structures on cycle yet"}</td></tr> : null}
        </tbody>
      </table>
    </div>
  );
}

// ---------------- Full itemised inspection form ----------------
function InspectionForm({ structure, onCancel, onSubmitted }) {
  const { user } = useAuth();
  const [tmpl, setTmpl] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    scaffold_type: "Independent",
    duty_class: "General purpose (2.0 kN/m²)",
    scaffold_classification: "TG20:21 compliant",
    special_purpose: "",
    scafftag_updated: true,
    further_notes: "",
    inspector_signature: "",
    inspector_print_name: user?.name || "",
    inspector_position: "Competent person (TWC)",
    inspector_cisrs_ref: "",
    client_name: "",
    client_position: "Site engineer",
    client_signature: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/inspections/template").then((r) => {
      setTmpl(r.data);
      setItems(r.data.items.map((it) => ({ ...it, grade: "", comment: "", photos: [] })));
    });
  }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, patch) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  // categories grouping
  const grouped = useMemo(() => {
    const out = {};
    items.forEach((it) => {
      if (!out[it.category]) out[it.category] = [];
      out[it.category].push(it);
    });
    return out;
  }, [items]);

  // counters
  const counts = useMemo(() => {
    const c = { CAP: 0, AN: 0, M: 0, NI: 0, PA: 0, SC: 0, "N/A": 0, "": 0 };
    items.forEach((it) => { c[it.grade || ""] = (c[it.grade || ""] || 0) + 1; });
    return c;
  }, [items]);

  const totalGraded = items.length - counts[""];
  const willTagOut = counts.SC > 0;

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        structure_id: structure.id,
        ...form,
        items: items.map((it) => ({
          item_no: it.item_no,
          category: it.category,
          label: it.label,
          grade: it.grade || "",
          comment: it.comment || "",
          photos: it.photos || [],
        })),
        photos: [],
      };
      const { data } = await api.post("/inspections", payload);
      onSubmitted?.(data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  if (!tmpl) return <div style={{ padding: 24 }}>Loading checklist…</div>;

  const ready = form.inspector_signature && form.inspector_print_name.trim() && totalGraded > 0;

  return (
    <div data-testid="inspection-form" style={{ padding: "24px 24px 96px" }}>
      <button className="btn-ghost" onClick={onCancel} style={{ marginBottom: 12 }} data-testid="insp-back"><ChevronLeft size={14} style={{ verticalAlign: -2 }} /> Back to tracker</button>

      <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>WEEKLY SCAFFOLD INSPECTION · APPENDIX A</div>
      <h1 style={{ fontSize: 24, fontWeight: 900, margin: "4px 0 0" }}>{structure.description}</h1>
      <div className="mono" style={{ color: "#64748b", fontSize: 12 }}>{structure.id} · {structure.area_name} · {structure.zone_classification} · {structure.price_schedule_ref}</div>

      {/* Progress + tag-out warning */}
      <div className="card" style={{ padding: 12, marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }} data-testid="insp-progress">
        <div className="mono" style={{ fontSize: 12, color: "#475569" }}>
          Graded {totalGraded} of {items.length} items
          {" · "}
          {Object.entries(counts).filter(([k, v]) => v > 0 && k !== "").map(([k, v]) => (
            <span key={k} className={`badge ${GRADE_BADGE[k] || "badge-slate"}`} style={{ marginRight: 4 }}>{k}: {v}</span>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            data-testid="insp-mark-all-cap"
            className="btn-ghost"
            onClick={() => setItems((arr) => arr.map((it) => ({ ...it, grade: "CAP" })))}
            style={{ padding: "6px 12px", fontWeight: 700 }}
          >
            Mark all CAP
          </button>
          {willTagOut ? (
            <div className="badge badge-red" style={{ padding: "6px 10px" }} data-testid="insp-tagout-warning">
              <AlertTriangle size={12} /> SC grade detected — scaffold WILL BE TAGGED OUT on submit
            </div>
          ) : null}
        </div>
      </div>
      <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
        Tip — bulk-CAP a whole section (or all sections) to fly through the checklist, then downgrade only the exceptions. Signature stays mandatory.
      </div>

      {/* Header fields */}
      <div className="card" style={{ padding: 16, marginTop: 14 }} data-testid="insp-header-card">
        <h3 style={{ marginTop: 0 }}>Header</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <FieldText label="Scaffold type" value={form.scaffold_type} onChange={(v) => setF("scaffold_type", v)} testid="insp-scaffold-type" />
          <FieldText label="Duty / loading class" value={form.duty_class} onChange={(v) => setF("duty_class", v)} testid="insp-duty" />
          <FieldText label="Scaffold classification (TG20:21 / bespoke)" value={form.scaffold_classification} onChange={(v) => setF("scaffold_classification", v)} testid="insp-classification" />
          <FieldText label="Special purpose" value={form.special_purpose} onChange={(v) => setF("special_purpose", v)} testid="insp-special" />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
          <input type="checkbox" checked={form.scafftag_updated} onChange={(e) => setF("scafftag_updated", e.target.checked)} data-testid="insp-scafftag" /> Scafftag updated on structure
        </label>
      </div>

      {/* Checklist by category */}
      {Object.entries(grouped).map(([cat, list]) => (
        <CategoryBlock key={cat} title={cat} items={list} setItem={(item_no, patch) => {
          const i = items.findIndex((x) => x.item_no === item_no);
          if (i >= 0) setItem(i, patch);
        }} grades={tmpl.grades} onMarkAllCAP={() => {
          setItems((arr) => arr.map((it) => it.category === cat ? { ...it, grade: "CAP" } : it));
        }} />
      ))}

      {/* Notes */}
      <div className="card" style={{ padding: 16, marginTop: 14 }}>
        <h3 style={{ marginTop: 0 }}>Further notes & observations</h3>
        <textarea data-testid="insp-notes" className="input-base" rows={4} value={form.further_notes} onChange={(e) => setF("further_notes", e.target.value)} placeholder="Anything else the supervisor / contracts manager should know…" />
      </div>

      {/* Conformity */}
      <div className="card" style={{ padding: 14, marginTop: 14, background: "#f8fafc" }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Conformity statement</div>
        <div data-testid="insp-conformity" style={{ marginTop: 6, fontSize: 13, color: "#334155", lineHeight: 1.5 }}>{tmpl.conformity_statement}</div>
      </div>

      {/* Sign-offs */}
      <div className="card" style={{ padding: 16, marginTop: 14 }} data-testid="insp-signoff-card">
        <h3 style={{ marginTop: 0 }}>Sign-off</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <FieldText label="Inspector print name" value={form.inspector_print_name} onChange={(v) => setF("inspector_print_name", v)} testid="insp-inspector-name" />
            <div style={{ height: 10 }} />
            <FieldText label="Inspector position" value={form.inspector_position} onChange={(v) => setF("inspector_position", v)} testid="insp-inspector-position" />
            <div style={{ height: 10 }} />
            <FieldText label="CISRS / competent ref" value={form.inspector_cisrs_ref} onChange={(v) => setF("inspector_cisrs_ref", v)} testid="insp-inspector-cisrs" />
            <div style={{ height: 10 }} />
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569", marginBottom: 4 }}>Inspector signature (required)</div>
            <SignaturePad value={form.inspector_signature} onChange={(v) => setF("inspector_signature", v)} testid="insp-inspector-signature" />
          </div>
          <div>
            <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>OPTIONAL — client (McCain) can also sign here at time of inspection, or via the structure detail page later.</div>
            <FieldText label="Client name" value={form.client_name} onChange={(v) => setF("client_name", v)} testid="insp-client-name" />
            <div style={{ height: 10 }} />
            <FieldText label="Client position" value={form.client_position} onChange={(v) => setF("client_position", v)} testid="insp-client-position" />
            <div style={{ height: 10 }} />
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569", marginBottom: 4 }}>Client signature (optional)</div>
            <SignaturePad value={form.client_signature} onChange={(v) => setF("client_signature", v)} testid="insp-client-signature" />
          </div>
        </div>
      </div>

      {err ? <div data-testid="insp-error" className="badge badge-red" style={{ padding: "8px 12px", marginTop: 12 }}>{String(err)}</div> : null}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, gap: 8 }}>
        <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button data-testid="insp-submit" className="btn-primary" disabled={!ready || busy} onClick={submit} style={{ height: 52, minWidth: 260, fontWeight: 800 }}>
          {busy ? "Saving…" : willTagOut ? "Submit + TAG OUT scaffold" : "Submit inspection report"}
        </button>
      </div>
      <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginTop: 8, textAlign: "right" }}>
        On submit: PDF report is generated · structure next-due auto-set +7 days · any SC tags the scaffold out (do-not-use).
      </div>
    </div>
  );
}

function FieldText({ label, value, onChange, testid }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569", marginBottom: 4 }}>{label}</div>
      <input className="input-base" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function CategoryBlock({ title, items, setItem, grades, onMarkAllCAP }) {
  const safeKey = title.split(".")[0].trim();
  return (
    <div className="card" style={{ padding: 0, marginTop: 14 }} data-testid={`cat-${safeKey}`}>
      <div style={{ background: "#f1f5f9", padding: "8px 14px", fontWeight: 700, color: "#0f172a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>{title}</span>
        <button
          type="button"
          data-testid={`mark-section-cap-${safeKey}`}
          className="btn-ghost"
          onClick={onMarkAllCAP}
          style={{ padding: "4px 10px", fontSize: 12, fontWeight: 700 }}
        >
          Mark section CAP
        </button>
      </div>
      <div>
        {items.map((it) => (
          <ItemRow key={it.item_no} item={it} setItem={(patch) => setItem(it.item_no, patch)} grades={grades} />
        ))}
      </div>
    </div>
  );
}

function ItemRow({ item, setItem, grades }) {
  const color = item.grade === "SC" ? SC_PA_COLOUR.SC : item.grade === "PA" ? SC_PA_COLOUR.PA : null;
  const fileRef = React.useRef();
  const camRef = React.useRef();
  const needsPhoto = (item.grade === "PA" || item.grade === "SC") && (!item.photos || item.photos.length === 0);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setItem({ photos: [...(item.photos || []), r.result] });
    r.readAsDataURL(f);
    e.target.value = "";
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "32px 1fr 200px", gap: 10, padding: "10px 14px", borderTop: "1px solid #e2e8f0", alignItems: "start" }} data-testid={`insp-item-${item.item_no}`}>
      <div className="mono" style={{ color: "#94a3b8", fontWeight: 600 }}>{item.item_no}</div>
      <div>
        <div style={{ fontWeight: 600, color: color || "#0f172a" }}>{item.label}</div>
        <input data-testid={`insp-item-${item.item_no}-comment`} className="input-base" style={{ marginTop: 6, fontSize: 13 }} placeholder="Comment (optional)" value={item.comment} onChange={(e) => setItem({ comment: e.target.value })} />
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onPhoto} data-testid={`insp-item-${item.item_no}-upload-input`} />
        <input ref={camRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={onPhoto} data-testid={`insp-item-${item.item_no}-camera-input`} />
        <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" data-testid={`insp-item-${item.item_no}-take`} className="btn-ghost" style={{ padding: "4px 10px", fontSize: 12, borderColor: needsPhoto ? "#dc2626" : "#cbd5e1", color: needsPhoto ? "#b91c1c" : "#0f172a", fontWeight: needsPhoto ? 700 : 500 }} onClick={() => camRef.current?.click()}>
            📷 Take photo
          </button>
          <button type="button" data-testid={`insp-item-${item.item_no}-upload`} className="btn-ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => fileRef.current?.click()}>
            Upload
          </button>
          {needsPhoto ? <span className="mono" style={{ fontSize: 10, color: "#b91c1c", fontWeight: 700 }} data-testid={`insp-item-${item.item_no}-evidence-prompt`}>EVIDENCE PHOTO RECOMMENDED FOR {item.grade}</span> : null}
          {(item.photos || []).map((p, idx) => (
            <div key={idx} style={{ position: "relative" }}>
              <img src={p} alt="evidence" style={{ height: 44, width: 64, objectFit: "cover", border: "1px solid #cbd5e1" }} data-testid={`insp-item-${item.item_no}-thumb-${idx}`} />
              <button type="button" onClick={() => setItem({ photos: item.photos.filter((_, i) => i !== idx) })} style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", border: "none", background: "#dc2626", color: "white", fontSize: 10, cursor: "pointer", fontWeight: 700 }} data-testid={`insp-item-${item.item_no}-remove-${idx}`}>×</button>
            </div>
          ))}
        </div>
      </div>
      <div>
        <select
          data-testid={`insp-item-${item.item_no}-grade`}
          className="input-base"
          style={{ borderColor: color || "#cbd5e1", color: color || "#0f172a", fontWeight: color ? 700 : 500 }}
          value={item.grade}
          onChange={(e) => setItem({ grade: e.target.value })}
        >
          <option value="">— grade —</option>
          {grades.map((g) => (
            <option key={g.code} value={g.code}>{g.code} · {g.label.slice(0, 36)}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
